import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>({});
  const [counters, setCounters] = useState({
    cases: 10000,
    satisfaction: 93,
    repeat: 85,
    doctors: 0
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-animate') || '';
            setIsVisible(prev => ({ ...prev, [id]: true }));
            
            // カウンターアニメーション
            if (id === 'stats') {
              const animateCounter = (target: number, setter: (value: number) => void, duration = 2000) => {
                let start = 0;
                const increment = target / (duration / 50);
                const timer = setInterval(() => {
                  start += increment;
                  if (start >= target) {
                    setter(target);
                    clearInterval(timer);
                  } else {
                    setter(Math.floor(start));
                  }
                }, 50);
              };

              setTimeout(() => {
                animateCounter(10000, (value) => setCounters(prev => ({ ...prev, cases: value })));
                animateCounter(93, (value) => setCounters(prev => ({ ...prev, satisfaction: value })));
                animateCounter(85, (value) => setCounters(prev => ({ ...prev, repeat: value })));
                animateCounter(8, (value) => setCounters(prev => ({ ...prev, doctors: value })));
              }, 500);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqData = [
    {
      q: "初回カウンセリングは本当に無料ですか?",
      a: "はい、初回カウンセリングは完全無料です。お肌の悩みやご希望をじっくりとお聞かせください。"
    },
    {
      q: "痛みが心配です。どのような対策がありますか?",
      a: "最新の無痛技術と麻酔クリームにより、痛みを最小限に抑えた施術を行っております。"
    },
    {
      q: "施術Afterのダウンタイムはどのくらいですか?",
      a: "施術内容により異なりますが、多くの場合1〜3日程度です。詳しくはカウンセリング時にご説明いたします。"
    },
    {
      q: "料金の支払い方法を教えてください。",
      a: "現金、クレジットカード、医療ローンをご利用いただけます。分割払いのご相談も承ります。"
    },
    {
      q: "アフターケアはありますか?",
      a: "施術After 1ヶ月間の無料アフターケアと、24時間対応の相談ホットラインをご用意しています。"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://public.readdy.ai/ai/img_res/1f49fc9f-f6e5-42bb-8616-508fbb6904e8.png" alt="美容クリニック" className="h-10 w-10 mr-3" />
            <span className="text-xl font-bold text-gray-900">エレガンス美容クリニック</span>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm">
            <a href="#treatment" className="text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer">施術メニュー</a>
            <a href="#doctors" className="text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer">医師紹介</a>
            <a href="#results" className="text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer">症例実績</a>
            <a href="#pricing" className="text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer">料金</a>
            <a href="#access" className="text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer">アクセス</a>
          </div>
          <button className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-6 py-2 rounded-full font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
            無料カウンセリング予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/ceb7fcd68dd7219ecbb5d4aa1e9834a0.jpg" 
            alt="高級美容クリニック内装" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className={`transform transition-all duration-1500 ${isVisible['hero'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="hero">
            <h1 className="text-5xl md:text-7xl font-serif mb-6 text-white leading-tight drop-shadow-2xl">
              あなたの理想を、<br/>
              <span className="text-6xl md:text-8xl font-bold">医療の力で</span><br/>
              叶える
            </h1>
            <p className="text-xl md:text-2xl text-yellow-300 mb-8 font-light drop-shadow-lg">
              創業15年、症例数10,000件超の確かな実績
            </p>
            
            <div className="mb-12">
              <a href="#booking" className="inline-block bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-10 py-3 rounded-full text-lg font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap shadow-xl">
                今すぐ無料カウンセリングを予約
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <p className="text-yellow-200 mt-4 text-sm drop-shadow-lg">所要時間：約30分 / 完全個室対応</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-16">
              {[
                {
                  title: "シワ・たるみ改善",
                  before: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20mouth%20area%20showing%20deep%20nasolabial%20folds%20smile%20lines%20wrinkles%20around%20mouth%20before%20anti%20aging%20treatment%20medical%20photography%20frontal%20view%20neutral%20white%20background&width=400&height=300&seq=nasolabial-before-v2&orientation=landscape",
                  after: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20mouth%20area%20smooth%20skin%20reduced%20nasolabial%20folds%20no%20smile%20lines%20after%20anti%20aging%20treatment%20medical%20photography%20same%20frontal%20view%20neutral%20white%20background&width=400&height=300&seq=nasolabial-after-v2&orientation=landscape"
                },
                {
                  title: "シミ・くすみ除去",
                  before: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20cheek%20area%20with%20multiple%20dark%20age%20spots%20pigmentation%20sun%20damage%20before%20laser%20treatment%20medical%20photography%20frontal%20view%20neutral%20white%20background&width=400&height=300&seq=cheek-spot-before-v2&orientation=landscape",
                  after: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20cheek%20area%20clear%20bright%20even%20skin%20tone%20no%20dark%20spots%20after%20laser%20treatment%20medical%20photography%20same%20frontal%20view%20neutral%20white%20background&width=400&height=300&seq=cheek-spot-after-v2&orientation=landscape"
                },
                {
                  title: "毛穴・ニキビ跡",
                  before: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20nose%20and%20cheek%20area%20large%20visible%20pores%20acne%20scars%20rough%20texture%20before%20treatment%20medical%20photography%20neutral%20background&width=400&height=300&seq=before-pore-003&orientation=landscape",
                  after: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20nose%20and%20cheek%20area%20refined%20small%20pores%20smooth%20texture%20after%20treatment%20medical%20photography%20same%20angle%20neutral%20background&width=400&height=300&seq=after-pore-003&orientation=landscape"
                }
              ].map((item, index) => (
                <div key={index} className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-500 shadow-xl">
                  <h3 className="text-yellow-600 font-bold text-lg mb-4 text-center">{item.title}</h3>
                  <div className="relative overflow-hidden rounded-xl">
                    <div className="flex">
                      <div className="w-1/2 relative">
                        <div className="w-full h-32">
                          <img src={item.before} alt="Before" className="w-full h-full object-cover object-center" />
                        </div>
                        <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">Before</div>
                      </div>
                      <div className="w-1/2 relative">
                        <div className="w-full h-32">
                          <img src={item.after} alt="After" className="w-full h-full object-cover object-center" />
                        </div>
                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">After</div>
                      </div>
                    </div>
                    <div className="absolute inset-y-0 left-1/2 w-1 bg-white transform -translate-x-px shadow-lg"></div>
                  </div>
                  <p className="text-center text-gray-600 text-sm mt-3">施術After 1ヶ月の結果</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem & Empathy Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['problems'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="problems">
            <span className="inline-block bg-white border-2 border-yellow-500 text-yellow-600 px-6 py-2 rounded-full text-sm font-medium mb-8">
              Problem - あなたのお悩み
            </span>
            <h2 className="text-4xl md:text-6xl font-serif mb-12 text-gray-900 leading-tight">
              こんなお悩み、<br/>
              ありませんか？<br/>
              <span className="text-yellow-600">私たちが解決します</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "年齢とともに深くなるシワ・たるみ",
              "メイクでも隠しきれないシミ・くすみ",
              "目立つ毛穴・ニキビ跡の凸凹",
              "人と会うのが憂鬱になった",
              "写真を撮られるのが嫌になった",
              "若い頃の自分が忘れられない"
            ].map((concern, index) => (
              <div key={index} className={`bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform ${isVisible['problems'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-yellow-500 text-2xl mr-4"></i>
                  <p className="text-gray-800 font-medium">{concern}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              美容に関するお悩みは、一人で抱え込む必要はありません。<br/>
              あなたの悩みに真摯に向き合い、<br/>
              <strong className="text-yellow-600">医療の力で確実に解決</strong>いたします。
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`mb-16 transform transition-all duration-1000 ${isVisible['solutions'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="solutions">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-12">
              <h2 className="text-4xl md:text-6xl font-serif text-gray-900 leading-tight mb-6 lg:mb-0">
                3つの約束
              </h2>
              <p className="text-gray-600 text-lg max-w-md">
                医療技術×ホスピタリティで、<br/>あなたの美を最大限に引き出します
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className={`lg:col-span-1 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-500 shadow-lg transform ${isVisible['solutions'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
              <img 
                src="https://readdy.ai/api/search-image?query=advanced%20medical%20laser%20equipment%20modern%20beauty%20clinic%20technology%20FDA%20approved%20devices%20professional%20medical%20instruments%20white%20background&width=400&height=300&seq=equipment-001&orientation=landscape" 
                alt="最新医療機器" 
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">最新医療機器</h3>
              <p className="text-gray-700">厚生労働省認可の最先端機器のみ使用。安全性と効果を両立した治療を提供します。</p>
            </div>

            <div className={`bg-white rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-500 shadow-lg border border-gray-100 transform ${isVisible['solutions'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '200ms' }}>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">経験豊富な医師</h3>
              <div className="flex justify-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20female%20doctor%20in%20white%20coat%20smiling%20confident%20medical%20portrait%20Japanese%20physician%20beauty%20specialist%20clinic%20background&width=200&height=200&seq=doctor-001&orientation=squarish" 
                  alt="医師" 
                  className="w-32 h-32 rounded-full object-cover shadow-lg"
                />
              </div>
              <p className="text-gray-700 text-center">形成外科専門医 / 美容外科学会認定医<br/>症例数15,000件超の確かな技術</p>
            </div>

            <div className={`bg-white rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-500 shadow-lg border border-gray-100 transform ${isVisible['solutions'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '400ms' }}>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">完全個室対応</h3>
              <img 
                src="https://readdy.ai/api/search-image?query=luxury%20private%20consultation%20room%20beauty%20clinic%20elegant%20white%20interior%20comfortable%20seating%20professional%20medical%20environment%20privacy&width=400&height=200&seq=room-001&orientation=landscape" 
                alt="個室カウンセリングルーム" 
                className="w-full h-40 object-cover rounded-xl mb-4"
              />
              <p className="text-gray-700 text-center">プライバシー完全保護<br/>リラックスできる上質な空間</p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Menu & Pricing */}
      <section id="treatment" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['treatment'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="treatment">
            <span className="inline-block bg-white border-2 border-yellow-500 text-yellow-600 px-6 py-2 rounded-full text-sm font-medium mb-8">
              Treatment Menu - 施術メニュー
            </span>
            <h2 className="text-4xl md:text-6xl font-serif mb-12 text-gray-900 leading-tight">
              あなたに最適な<br/>
              <span className="text-yellow-600">美容医療</span>を
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              最新の医療技術と豊富な経験で、一人ひとりのお悩みに合わせた最適な治療をご提案します
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                category: "シワ・たるみ",
                name: "ヒアルロン酸注入",
                image: "https://readdy.ai/api/search-image?query=hyaluronic%20acid%20injection%20syringe%20medical%20beauty%20treatment%20professional%20clinic%20equipment%20white%20background%20elegant%20presentation&width=400&height=300&seq=treatment-hyaluronic-001&orientation=landscape",
                description: "即効性のある若返り治療。ほうれい線や目元のシワを自然に改善します。",
                price: "¥55,000",
                duration: "30分",
                downtime: "ほぼなし"
              },
              {
                category: "シワ・たるみ",
                name: "ハイフ（HIFU）治療",
                image: "https://readdy.ai/api/search-image?query=HIFU%20ultrasound%20beauty%20device%20modern%20medical%20equipment%20professional%20clinic%20technology%20white%20background%20elegant%20presentation&width=400&height=300&seq=treatment-hifu-002&orientation=landscape",
                description: "メスを使わないリフトアップ。超音波で肌の深層から引き締めます。",
                price: "¥88,000",
                duration: "60分",
                downtime: "1-2日"
              },
              {
                category: "シミ・くすみ",
                name: "IPLレーザー治療",
                image: "https://readdy.ai/api/search-image?query=IPL%20laser%20device%20photofacial%20equipment%20modern%20beauty%20clinic%20technology%20professional%20medical%20instrument%20white%20background&width=400&height=300&seq=treatment-ipl-003&orientation=landscape",
                description: "光治療でシミ・そばかす・くすみを改善。透明感のある肌へ。",
                price: "¥38,000",
                duration: "40分",
                downtime: "1週間"
              },
              {
                category: "シミ・くすみ",
                name: "ピコレーザー",
                image: "https://readdy.ai/api/search-image?query=pico%20laser%20device%20advanced%20beauty%20equipment%20modern%20medical%20technology%20professional%20clinic%20instrument%20white%20background&width=400&height=300&seq=treatment-pico-004&orientation=landscape",
                description: "最新のレーザー技術。頑固なシミやタトゥー除去に効果的です。",
                price: "¥68,000",
                duration: "30分",
                downtime: "3-5日"
              },
              {
                category: "毛穴・ニキビ跡",
                name: "フラクショナルレーザー",
                image: "https://readdy.ai/api/search-image?query=fractional%20laser%20device%20skin%20resurfacing%20equipment%20modern%20beauty%20clinic%20technology%20professional%20medical%20instrument%20white%20background&width=400&height=300&seq=treatment-fractional-005&orientation=landscape",
                description: "毛穴の開きやニキビ跡を改善。肌の再生を促進します。",
                price: "¥78,000",
                duration: "50分",
                downtime: "3-7日"
              },
              {
                category: "毛穴・ニキビ跡",
                name: "ダーマペン4",
                image: "https://readdy.ai/api/search-image?query=dermapen%20microneedling%20device%20professional%20beauty%20equipment%20modern%20medical%20technology%20clinic%20instrument%20white%20background&width=400&height=300&seq=treatment-dermapen-006&orientation=landscape",
                description: "微細な針で肌を刺激し、コラーゲン生成を促進。毛穴レスな肌へ。",
                price: "¥45,000",
                duration: "45分",
                downtime: "2-3日"
              },
              {
                category: "美白・美肌",
                name: "美容点滴",
                image: "https://readdy.ai/api/search-image?query=IV%20drip%20beauty%20infusion%20medical%20equipment%20professional%20clinic%20setup%20elegant%20presentation%20white%20background&width=400&height=300&seq=treatment-drip-007&orientation=landscape",
                description: "高濃度ビタミンCなどの美容成分を直接体内へ。内側から輝く肌に。",
                price: "¥15,000",
                duration: "30分",
                downtime: "なし"
              },
              {
                category: "美白・美肌",
                name: "水光注射",
                image: "https://readdy.ai/api/search-image?query=water%20light%20injection%20device%20skin%20booster%20equipment%20modern%20beauty%20clinic%20technology%20professional%20medical%20instrument%20white%20background&width=400&height=300&seq=treatment-water-008&orientation=landscape",
                description: "ヒアルロン酸やビタミンを肌に注入。潤いとツヤのある肌へ。",
                price: "¥58,000",
                duration: "40分",
                downtime: "1-2日"
              },
              {
                category: "総合治療",
                name: "オーダーメイド美容治療",
                image: "https://readdy.ai/api/search-image?query=luxury%20beauty%20consultation%20room%20professional%20medical%20equipment%20modern%20clinic%20interior%20elegant%20treatment%20space%20white%20background&width=400&height=300&seq=treatment-custom-009&orientation=landscape",
                description: "あなたのお悩みに合わせた複合治療プラン。医師が最適な組み合わせをご提案。",
                price: "¥150,000〜",
                duration: "90-120分",
                downtime: "要相談"
              }
            ].map((treatment, index) => (
              <div key={index} className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 ${isVisible['treatment'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="relative h-56">
                  <img 
                    src={treatment.image} 
                    alt={treatment.name} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {treatment.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{treatment.name}</h3>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">{treatment.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-gray-700">
                      <i className="ri-time-line text-yellow-500 mr-2 w-5 h-5 flex items-center justify-center"></i>
                      <span>施術時間：{treatment.duration}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <i className="ri-calendar-line text-yellow-500 mr-2 w-5 h-5 flex items-center justify-center"></i>
                      <span>ダウンタイム：{treatment.downtime}</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-6 flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">料金（税込）</p>
                      <p className="text-3xl font-bold text-yellow-600">{treatment.price}</p>
                    </div>
                    <button className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
                      詳細を見る
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-white rounded-2xl p-8 max-w-4xl mx-auto shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">初回限定特典</h3>
              <p className="text-gray-700 mb-6">
                初めてご来院の方限定で、すべての施術が<span className="text-yellow-600 font-bold text-xl">20%OFF</span>
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-yellow-50 px-6 py-3 rounded-full">
                  <i className="ri-check-line text-yellow-600 mr-2"></i>
                  <span className="text-gray-800 font-medium">無料カウンセリング</span>
                </div>
                <div className="bg-yellow-50 px-6 py-3 rounded-full">
                  <i className="ri-check-line text-yellow-600 mr-2"></i>
                  <span className="text-gray-800 font-medium">アフターケア無料</span>
                </div>
                <div className="bg-yellow-50 px-6 py-3 rounded-full">
                  <i className="ri-check-line text-yellow-600 mr-2"></i>
                  <span className="text-gray-800 font-medium">医療ローン対応</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Introduction */}
      <section id="doctors" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`mb-16 transform transition-all duration-1000 ${isVisible['doctors'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="doctors">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-12">
              <h2 className="text-4xl md:text-6xl font-serif text-gray-900 leading-tight mb-6 lg:mb-0">
                信頼の医療チーム
              </h2>
              <p className="text-gray-600 text-lg max-w-md">
                形成外科・美容外科の専門医資格を持つ医師が、<br/>あなた専属で担当します
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className={`lg:col-span-1 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 text-white hover:transform hover:scale-105 transition-all duration-500 shadow-xl transform ${isVisible['doctors'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
              <span className="text-yellow-400 text-sm font-medium">院長 / 総院長</span>
              <h3 className="text-3xl font-bold mb-6">田中 美咲医師</h3>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20Japanese%20female%20doctor%20in%20white%20medical%20coat%20confident%20smile%20medical%20portrait%20beauty%20clinic%20director%20experienced%20physician&width=300&height=400&seq=director-001&orientation=portrait" 
                alt="田中美咲院長" 
                className="w-full h-80 object-cover rounded-xl mb-6"
              />
              <ul className="text-sm space-y-2 text-gray-300">
                <li>• 東京大学医学部卒業</li>
                <li>• 形成外科専門医</li>
                <li>• 美容外科学会認定医</li>
                <li>• 症例数15,000件超</li>
                <li>• 医学博士</li>
              </ul>
            </div>

            <div className={`bg-white rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-500 shadow-lg border border-gray-100 transform ${isVisible['doctors'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '200ms' }}>
              <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-medium">専門医</span>
              <div className="flex justify-center my-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20Japanese%20male%20doctor%20smiling%20medical%20portrait%20white%20coat%20beauty%20clinic%20specialist%20experienced%20physician&width=200&height=200&seq=doctor-002&orientation=squarish" 
                  alt="佐藤医師" 
                  className="w-32 h-32 rounded-full object-cover shadow-lg"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">佐藤 健太医師</h3>
              <p className="text-gray-700 text-center text-sm">レーザー治療 / 注入治療<br/>慶應義塾大学医学部卒</p>
            </div>

            <div className={`bg-white rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-500 shadow-lg border border-gray-100 transform ${isVisible['doctors'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '400ms' }}>
              <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-medium">専門医</span>
              <div className="flex justify-center my-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20Japanese%20female%20doctor%20medical%20portrait%20white%20coat%20beauty%20clinic%20dermatology%20specialist%20confident%20smile%20experienced%20physician&width=200&height=200&seq=doctor-003&orientation=squarish" 
                  alt="山田医師" 
                  className="w-32 h-32 rounded-full object-cover shadow-lg"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">山田 亜紀医師</h3>
              <p className="text-gray-700 text-center text-sm">皮膚科・美容皮膚科<br/>順天堂大学医学部卒</p>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Results */}
      <section id="results" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['results'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="results">
            <div className="flex items-center justify-center mb-6">
              <i className="ri-diamond-line text-yellow-500 text-2xl mr-3"></i>
              <span className="text-yellow-600 font-medium">Results - 症例実績</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-serif text-gray-900 leading-tight">
              10,000件超の症例が証明する、<br/>確かな技術
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                type: "シワ改善",
                treatment: "ヒアルロン酸注入",
                period: "1ヶ月After",
                before: "https://readdy.ai/api/search-image?query=close%20up%20woman%20eye%20area%20showing%20crow%20feet%20wrinkles%20and%20forehead%20lines%20before%20hyaluronic%20acid%20injection%20medical%20photography%20neutral%20background&width=400&height=300&seq=result-wrinkle-before-001&orientation=landscape",
                after: "https://readdy.ai/api/search-image?query=close%20up%20woman%20eye%20area%20smooth%20skin%20no%20crow%20feet%20reduced%20wrinkles%20after%20hyaluronic%20acid%20injection%20medical%20photography%20same%20angle%20neutral%20background&width=400&height=300&seq=result-wrinkle-after-001&orientation=landscape"
              },
              {
                type: "シミ取り",
                treatment: "IPLレーザー",
                period: "2週間After",
                before: "https://readdy.ai/api/search-image?query=close%20up%20woman%20cheek%20and%20nose%20area%20with%20age%20spots%20dark%20pigmentation%20before%20IPL%20laser%20treatment%20medical%20photography%20frontal%20view%20neutral%20background&width=400&height=300&seq=result-spot-before-002&orientation=landscape",
                after: "https://readdy.ai/api/search-image?query=close%20up%20woman%20cheek%20and%20nose%20area%20clear%20bright%20skin%20no%20dark%20spots%20after%20IPL%20laser%20treatment%20medical%20photography%20same%20frontal%20view%20neutral%20background&width=400&height=300&seq=result-spot-after-002&orientation=landscape"
              },
              {
                type: "毛穴治療",
                treatment: "フラクショナルレーザー",
                period: "3ヶ月After",
                before: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20nose%20cheek%20area%20large%20open%20pores%20acne%20scars%20rough%20texture%20before%20fractional%20laser%20medical%20photography%20neutral%20background&width=400&height=300&seq=result-pore-before-003&orientation=landscape",
                after: "https://readdy.ai/api/search-image?query=extreme%20close%20up%20woman%20nose%20cheek%20area%20refined%20small%20pores%20smooth%20skin%20texture%20after%20fractional%20laser%20medical%20photography%20same%20angle%20neutral%20background&width=400&height=300&seq=result-pore-after-003&orientation=landscape"
              },
              {
                type: "フェイスリフト",
                treatment: "ハイフ治療",
                period: "2ヶ月After",
                before: "https://readdy.ai/api/search-image?query=close%20up%20woman%20lower%20face%20jawline%20area%20showing%20sagging%20jowls%20loose%20skin%20before%20HIFU%20treatment%20medical%20photography%20side%20profile%20neutral%20background&width=400&height=300&seq=result-lift-before-004&orientation=landscape",
                after: "https://readdy.ai/api/search-image?query=close%20up%20woman%20lower%20face%20jawline%20area%20lifted%20tight%20skin%20defined%20jawline%20after%20HIFU%20treatment%20medical%20photography%20same%20side%20profile%20neutral%20background&width=400&height=300&seq=result-lift-after-004&orientation=landscape"
              },
              {
                type: "ニキビ跡",
                treatment: "ピコレーザー",
                period: "6ヶ月After",
                before: "https://readdy.ai/api/search-image?query=close%20up%20woman%20cheek%20area%20with%20acne%20scars%20pitted%20skin%20texture%20ice%20pick%20scars%20before%20pico%20laser%20treatment%20medical%20photography%20neutral%20background&width=400&height=300&seq=result-acne-before-005&orientation=landscape",
                after: "https://readdy.ai/api/search-image?query=close%20up%20woman%20cheek%20area%20smooth%20clear%20skin%20reduced%20acne%20scars%20improved%20texture%20after%20pico%20laser%20treatment%20medical%20photography%20same%20angle%20neutral%20background&width=400&height=300&seq=result-acne-after-005&orientation=landscape"
              }
            ].slice(0, 5).map((result, index) => (
              <div key={index} className={`bg-gray-50 rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-500 shadow-lg transform ${isVisible['results'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-magic-line text-white text-sm"></i>
                  </div>
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">{result.type}</span>
                </div>
                
                <div className="relative overflow-hidden rounded-xl mb-4">
                  <div className="flex">
                    <div className="w-1/2 relative">
                      <div className="w-full h-40">
                        <img src={result.before} alt="Before" className="w-full h-full object-cover object-center" />
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">Before</div>
                    </div>
                    <div className="w-1/2 relative">
                      <div className="w-full h-40">
                        <img src={result.after} alt="After" className="w-full h-full object-cover object-center" />
                      </div>
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">After</div>
                    </div>
                  </div>
                  <div className="absolute inset-y-0 left-1/2 w-1 bg-white transform -translate-x-px shadow-lg"></div>
                </div>
                
                <p className="text-gray-600 text-sm text-center">
                  <strong>施術名：</strong>{result.treatment}<br/>
                  <strong>期間：</strong>{result.period}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['testimonials'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="testimonials">
            <span className="inline-block bg-white border-2 border-yellow-500 text-yellow-600 px-6 py-2 rounded-full text-sm font-medium mb-8">
              Testimonials - お客様の声
            </span>
            <h2 className="text-4xl md:text-6xl font-serif text-gray-900 leading-tight">
              患者様の笑顔が、<br/>私たちの誇りです
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "M.K様",
                age: "40代女性",
                treatment: "シワ・たるみ治療",
                rating: 5,
                comment: "20年ぶりに同窓会に参加できました。みんなに「若くなったね」と言われて本当に嬉しかったです。先生やスタッフの皆さんが親身になってくださり、安心して治療を受けられました。",
                image: "https://readdy.ai/api/search-image?query=happy%20Japanese%20woman%20in%20her%2040s%20smiling%20confidently%20after%20beauty%20treatment%20natural%20makeup%20glowing%20skin%20testimonial%20portrait&width=200&height=200&seq=testimonial-001&orientation=squarish"
              },
              {
                name: "A.S様",
                age: "30代女性",
                treatment: "シミ取りレーザー",
                rating: 5,
                comment: "長年悩んでいたシミが綺麗になくなって、メイク時間が半分になりました！痛みもほとんどなく、ダウンタイムも短くて仕事に支障がありませんでした。",
                image: "https://readdy.ai/api/search-image?query=beautiful%20Japanese%20woman%20in%20her%2030s%20clear%20skin%20no%20makeup%20natural%20beauty%20testimonial%20portrait%20happy%20expression&width=200&height=200&seq=testimonial-002&orientation=squarish"
              },
              {
                name: "R.T様",
                age: "50代女性",
                treatment: "フェイスリフト",
                rating: 5,
                comment: "友人に勧められて来院しました。カウンセリングから施術まで、とても丁寧で安心できました。10歳は若返ったと主人にも褒められています。",
                image: "https://readdy.ai/api/search-image?query=elegant%20Japanese%20woman%20in%20her%2050s%20sophisticated%20smile%20after%20facelift%20treatment%20natural%20lighting%20testimonial%20portrait&width=200&height=200&seq=testimonial-003&orientation=squarish"
              },
              {
                name: "Y.H様",
                age: "20代女性",
                treatment: "ニキビ跡治療",
                rating: 5,
                comment: "学生時代からのニキビ跡に悩んでいましたが、治療Afterは毛穴も目立たなくなり、すっぴんに自信が持てるようになりました。ありがとうございました。",
                image: "https://readdy.ai/api/search-image?query=young%20Japanese%20woman%20in%20her%2020s%20clear%20perfect%20skin%20happy%20smile%20testimonial%20portrait%20natural%20beauty%20confidence&width=200&height=200&seq=testimonial-004&orientation=squarish"
              },
              {
                name: "C.N様",
                age: "60代女性",
                treatment: "総合美容治療",
                rating: 5,
                comment: "年齢を重ねることに不安がありましたが、こちらで治療を受けてから、外出することが楽になり、娘からも「お母さん、綺麗になったね」と言われます。",
                image: "https://readdy.ai/api/search-image?query=graceful%20Japanese%20woman%20in%20her%2060s%20elegant%20smile%20after%20beauty%20treatment%20refined%20appearance%20testimonial%20portrait&width=200&height=200&seq=testimonial-005&orientation=squarish"
              }
            ].slice(0, 5).map((testimonial, index) => (
              <div key={index} className={`bg-white rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-500 shadow-lg transform ${isVisible['testimonials'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="flex items-center mb-4">
                  <img src={testimonial.image} alt={testimonial.name} className="w-16 h-16 rounded-full object-cover mr-4" />
                  <div>
                    <h3 className="font-bold text-gray-900">{testimonial.name}</h3>
                    <p className="text-gray-600 text-sm">{testimonial.age}</p>
                    <div className="flex text-yellow-400 text-sm">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <i className="ri-double-quotes-l text-yellow-400 text-2xl"></i>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">{testimonial.comment}</p>
                <span className="text-yellow-600 text-sm font-medium">{testimonial.treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['stats'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="stats">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-8">数字で見る信頼の実績</h2>
            <p className="text-gray-600 text-lg">15年間で築き上げた確かな信頼と実績</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center bg-gray-50 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-5xl font-bold text-yellow-600 mb-4">
                {counters.cases.toLocaleString()}&nbsp;
                <span className="text-2xl">+</span>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-yellow-600 text-2xl"></i>
              </div>
              <p className="text-gray-900 font-bold">症例数</p>
            </div>

            <div className="text-center bg-gray-50 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-5xl font-bold text-yellow-600 mb-4">
                {counters.satisfaction}&nbsp;%
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-smile-line text-yellow-600 text-2xl"></i>
              </div>
              <p className="text-gray-900 font-bold">満足度</p>
            </div>

            <div className="text-center bg-gray-50 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-5xl font-bold text-yellow-600 mb-4">
                {counters.repeat}&nbsp;%
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refresh-line text-yellow-600 text-2xl"></i>
              </div>
              <p className="text-gray-900 font-bold">リピート率</p>
            </div>

            <div className="text-center bg-gray-50 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-300">
              <div className="text-5xl font-bold text-yellow-600 mb-4">
                {counters.doctors}
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-yellow-600 text-2xl"></i>
              </div>
              <p className="text-gray-900 font-bold">専門医数</p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Flow */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['flow'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="flow">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-8">施術の流れ</h2>
            <p className="text-gray-600 text-lg">初回から施術完了まで、安心のステップ</p>
          </div>

          <div className="space-y-8">
            {[
              {
                step: 1,
                title: "無料カウンセリング",
                description: "お悩みやご希望を詳しくお聞かせください。専門医が最適な治療プランをご提案いたします。",
                time: "30分"
              },
              {
                step: 2,
                title: "診察・検査",
                description: "肌質や症状を詳細に診察し、最適な治療方法を決定いたします。不安な点はお気軽にご相談ください。",
                time: "20分"
              },
              {
                step: 3,
                title: "治療計画の説明",
                description: "施術内容、期間、費用について詳しくご説明いたします。ご納得いただいてから治療を開始いたします。",
                time: "15分"
              },
              {
                step: 4,
                title: "施術",
                description: "経験豊富な専門医が、最新の医療機器を使用して丁寧に施術を行います。痛みを最小限に抑えた治療です。",
                time: "30-60分"
              },
              {
                step: 5,
                title: "アフターケア",
                description: "施術Afterの経過観察とアフターケアを行います。24時間対応の相談ホットラインもご利用いただけます。",
                time: "継続"
              }
            ].map((item, index) => (
              <div key={index} className={`flex items-start transform transition-all duration-1000 ${isVisible['flow'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${index * 200}ms` }}>
                <div className="flex-shrink-0 w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-6">
                  {item.step}
                </div>
                <div className="flex-grow bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">{item.time}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{item.description}</p>
                </div>
                {index < 4 && (
                  <div className="absolute left-8 mt-16 w-px h-8 bg-yellow-300 transform translate-x-px"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['faq'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="faq">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-8">よくある質問</h2>
            <p className="text-gray-600 text-lg">患者様からよくいただくご質問にお答えします</p>
          </div>

          <div className="space-y-4">
            {faqData.map((item, index) => (
              <div key={index} className={`bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 transform ${isVisible['faq'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                      Q
                    </div>
                    <span className="font-medium text-gray-900">{item.q}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-gray-500 text-2xl transform transition-transform duration-200 ${openFAQ === index ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-6 pb-4">
                    <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-500">
                      <p className="text-gray-700 leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-20 bg-gradient-to-br from-yellow-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`text-center mb-12 transform transition-all duration-1000 ${isVisible['booking'] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} data-animate="booking">
            <h2 className="text-4xl md:text-6xl font-serif text-gray-900 leading-tight mb-8">
              今すぐ、理想の自分へ
            </h2>
            <p className="text-xl text-gray-700 mb-12">
              無料カウンセリングで、あなた専用のプランをご提案
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <form 
              data-readdy-form
              action="https://readdy.ai/api/form/d5r6eumtc768qf4bqg9g"
              method="POST"
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const data = Object.fromEntries(formData.entries());
                
                fetch("https://readdy.ai/api/form/d5r6eumtc768qf4bqg9g", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                  body: new URLSearchParams(data as any).toString(),
                })
                .then(() => {
                  alert('予約申し込みを受け付けました。48時間以内にご連絡いたします。');
                  (e.target as HTMLFormElement).reset();
                })
                .catch(() => {
                  alert('送信に失敗しました。お電話でのお問い合わせをお願いします。');
                });
              }}
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">無料カウンセリング予約フォーム</h3>
                <p className="text-gray-600">24時間受付中・当日予約OK</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">お名前 *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">年齢</label>
                  <input
                    type="number"
                    name="age"
                    min="18"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                    placeholder="30"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">電話番号 *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ご希望の施術</label>
                <select
                  name="treatment"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">選択してください</option>
                  <option value="シワ・たるみ治療">シワ・たるみ治療</option>
                  <option value="シミ・くすみ治療">シミ・くすみ治療</option>
                  <option value="毛穴治療">毛穴治療</option>
                  <option value="ニキビ跡治療">ニキビ跡治療</option>
                  <option value="フェイスリフト">フェイスリフト</option>
                  <option value="総合美容治療">総合美容治療</option>
                  <option value="相談したい">まずは相談したい</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ご希望の日時</label>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="date"
                    name="preferred_date"
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                  />
                  <select
                    name="preferred_time"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">時間を選択</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="12:00">12:00</option>
                    <option value="14:00">14:00</option>
                    <option value="15:00">15:00</option>
                    <option value="16:00">16:00</option>
                    <option value="17:00">17:00</option>
                    <option value="18:00">18:00</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">お悩みやご質問</label>
                <textarea
                  name="message"
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="お肌のお悩みやご質問がございましたら、お気軽にご記入ください（最大500文字）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">※500文字以内でご入力ください</p>
              </div>

              <div className="text-center pt-6">
                <button
                  type="submit"
                  className="w-full md:w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-8 py-3 rounded-full text-base md:text-lg font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  無料カウンセリング予約
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
                <p className="text-gray-600 mt-4 text-sm">
                  ※48時間以内にご連絡いたします<br/>
                  お急ぎの場合は直接お電話ください:03-1234-5678
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <h3 className="text-2xl font-serif mb-6 leading-tight">
                美しさは、<br/>医療から。
              </h3>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">メニュー</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#treatment" className="hover:text-yellow-400 transition-colors cursor-pointer">施術一覧</a></li>
                <li><a href="#pricing" className="hover:text-yellow-400 transition-colors cursor-pointer">料金</a></li>
                <li><a href="#doctors" className="hover:text-yellow-400 transition-colors cursor-pointer">医師紹介</a></li>
                <li><a href="#access" className="hover:text-yellow-400 transition-colors cursor-pointer">アクセス</a></li>
                <li><a href="#recruit" className="hover:text-yellow-400 transition-colors cursor-pointer">採用情報</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">SNS</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-instagram-line text-yellow-400 mr-2"></i>
                  <span>Instagram</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-youtube-line text-yellow-400 mr-2"></i>
                  <span>YouTube</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-facebook-line text-yellow-400 mr-2"></i>
                  <span>Facebook</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 leading-tight">
                最新情報を<br/>お届け
              </h4>
              <div className="flex">
                <input
                  type="email"
                  placeholder="メールアドレスを入力"
                  className="flex-grow px-4 py-2 bg-gray-700/50 border border-yellow-400/50 rounded-l-full text-sm focus:outline-none focus:border-yellow-400"
                />
                <button className="bg-white text-gray-800 px-4 py-2 rounded-r-full hover:bg-yellow-400 transition-colors">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2024 エレガンス美容クリニック. All rights reserved.
              </p>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
          <div className="text-6xl md:text-8xl font-bold text-gray-700/10 text-center py-8">
            ELEGANCE BEAUTY CLINIC
          </div>
        </div>
      </footer>
    </div>
  );
}
